import { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";

import EcheckinDao from "@dao/EcheckinDao";
import StudentInfoDao from "@dao/StudentInfoDao";

import { DASH_LINKS } from "@const/AppCardLinks";
import { formatDate, getEpoch, getTodayWithTime } from "@utils/DateFormatter";
import { GlobalContext } from "@components/contextProvider/ContextProvider";
import { isWindowOpen } from "@utils/ActionControlWindows";
import type { AppObject } from "@/types/GlobalState";

// ─── Types ────────────────────────────────────────────────────────────────────

type IdField = string | number | undefined;

interface LocationDto {
    key: string | number;
    icId?: (string | number)[];
    feederLocationId?: string | number | null;
}

interface SchoolYearDto {
    key: string | number;
}

interface AppButton {
    label: string;
    href?: string | null;
}
interface BusStudent {
    key?: string;
    studentNumber?: string | number;
    firstName?: string;
    locationKey?: string | number;
    schoolId?: string | number;
    [key: string]: unknown;
}

interface HomeSchooledEntry {
    studentNumber: string | number;
    isHomeSchooled?: boolean;
}

interface BusRoutePayload {
    eligibleToRide?: boolean;
    [key: string]: unknown;
}

interface TransportationWindowPayload {
    annualPassOfferEndDate: string;
    checkinEndDate: string;
    checkinStartDate: string;
    [key: string]: unknown;
}

interface BusPassApplication {
    key: string | number;
    cardStatus?: string;
    [key: string]: unknown;
}

interface CsaPayload {
    value?: string;
    [key: string]: unknown;
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

const getLocationDto = (
    allLocationDtos: LocationDto[],
    schoolId: IdField,
    locationKey: IdField,
    _studentNumber: IdField
): LocationDto | null => {
    try {
        if (schoolId) {
            return (
                allLocationDtos.find((obj) =>
                    obj.icId?.some((id) => Number.parseInt(String(id), 10) === Number.parseInt(String(schoolId), 10))
                ) ?? null
            );
        }
        if (locationKey) {
            return (
                allLocationDtos.find(
                    (obj) => Number.parseInt(String(obj.key), 10) === Number.parseInt(String(locationKey), 10)
                ) ?? null
            );
        }
        return null;
    } catch (error: unknown) {
        console.error("Unable to retrieve a Location for Student", error);
        return null;
    }
};

const getIsHomeSchooled = (studentsHomeSchooled: HomeSchooledEntry[] | undefined, studentNumber: IdField): boolean => {
    try {
        const match = studentsHomeSchooled?.find(
            (entry) => Number.parseInt(String(entry.studentNumber), 10) === Number.parseInt(String(studentNumber), 10)
        );
        return match?.isHomeSchooled ?? false;
    } catch {
        return false;
    }
};

const getButtons = (startDate: string, endDate: string, studentNumber: IdField): AppButton[] | null => {
    if (!isWindowOpen(startDate, endDate)) return null;
    return [{ label: "BusApp_Btn1", href: `${DASH_LINKS.transportation}/${String(studentNumber)}` }];
};

const getBusPassStatus = (
    app: AppObject,
    csa: CsaPayload | null,
    hasApplied: BusPassApplication | null,
    window: TransportationWindowPayload,
    firstName: string | undefined
): AppObject => {
    const updatedApp = { ...app };

    if (hasApplied?.cardStatus === "PROCESSED") {
        updatedApp.status = "BusApp_BusPass";
        updatedApp.statusColor = "#4a8319";
        updatedApp.subBody = "BusApp_BusPassIssued";
        updatedApp.subBody2 = "BusApp_BusPassIssued2";
    } else if (hasApplied?.cardStatus === "UNPROCESSED") {
        updatedApp.status = "BusApp_Processing";
        updatedApp.statusColor = "#0669B3";
        updatedApp.subBody = "BusApp_BusPassProcessing";
    } else if (csa?.value === "N") {
        updatedApp.status = "BusApp_NotRiding";
        updatedApp.statusColor = "#CB321A";
        updatedApp.subBody = "BusApp_NotRidingReason3";
        updatedApp.subBodyRange = `(${firstName})`;
    } else {
        updatedApp.status = "BusApp_NotIssued";
        updatedApp.statusColor = "#707070";
        updatedApp.subBody = "BusApp_NoBusPass";
    }

    if (csa?.value === "YQ") {
        updatedApp.subBody3 = "BusApp_Quarterly";
        updatedApp.subBody3Range = formatDate(window.annualPassOfferEndDate, true, true);
    }
    if (csa?.value === "Y") {
        updatedApp.subBody3 = "BusApp_Annual";
        updatedApp.subBody3Range = null;
    }

    return updatedApp;
};

const fetchBusRoute = async (studentNumber: IdField, signal: AbortSignal): Promise<BusRoutePayload | null> => {
    try {
        const response = await StudentInfoDao<{ payload: BusRoutePayload }>({
            action: "studentBusRoutesRead",
            studentNumber,
            signal
        });
        return response?.data?.payload?.eligibleToRide ? response.data.payload : null;
    } catch (error: unknown) {
        if (!axios.isCancel(error)) {
            console.error("Unable to retrieve Bus Routes:\n", error);
        }
        return null;
    }
};

const fetchCsa = async (studentNumber: IdField, signal: AbortSignal): Promise<CsaPayload | null> => {
    try {
        const response = await StudentInfoDao<{ payload: CsaPayload }>({
            action: "customAttributeRead",
            attributeName: "TransportationOption",
            objectName: "Enrollment",
            params: { currentEnrollment: true },
            studentNumber,
            signal
        });
        return response?.data?.payload ?? null;
    } catch (error: unknown) {
        if (!axios.isCancel(error)) {
            console.error("Unable to retrieve CSA for student", error);
        }
        return null;
    }
};

const fetchHasApplied = async (
    studentNumber: IdField,
    schoolYearKey: string | number,
    signal: AbortSignal
): Promise<BusPassApplication | null> => {
    try {
        const [response, hResponse] = await Promise.all([
            EcheckinDao<{ payload: BusPassApplication[] }>({
                action: "busPassApplicationsByStudentNumber",
                schoolYearKey,
                studentNumber,
                signal
            }),
            EcheckinDao<{ payload: BusPassApplication[] }>({
                action: "histBusPassApplicationsByStudentNumber",
                schoolYearKey,
                studentNumber,
                signal
            })
        ]);

        const payload = [...(response?.data?.payload || []), ...(hResponse?.data?.payload || [])];

        if (payload.length > 0) {
            // Priority 1: Use the PROCESSED application for the current school year if it exists
            const processedCurrentYear = response?.data?.payload?.find((app) => app.cardStatus === "PROCESSED");
            if (processedCurrentYear) return processedCurrentYear;

            // Priority 2: Use the latest PROCESSED application overall
            const latestProcessed = payload
                .filter((app) => app.cardStatus === "PROCESSED")
                .sort((a, b) => Number.parseInt(String(b.key), 10) - Number.parseInt(String(a.key), 10))[0];
            if (latestProcessed) return latestProcessed;

            // Priority 3: Fall back to the latest application regardless of status
            payload.sort((a, b) => Number.parseInt(String(b.key), 10) - Number.parseInt(String(a.key), 10));
            return payload[0];
        }

        return null;
    } catch (error: unknown) {
        if (!axios.isCancel(error)) {
            console.error("Unable to retrieve Bus Pass Applications:\n", error);
        }
        return null;
    }
};

const shouldSkip = (
    studentNumber: IdField,
    enabled: boolean,
    schoolYearDto: SchoolYearDto | undefined,
    allLocationDtos: LocationDto[] | undefined,
    studentsHomeSchooled: HomeSchooledEntry[] | undefined,
    isGuardian: boolean
): boolean => {
    if (!studentNumber || !enabled || !isGuardian) return true;
    if (!schoolYearDto || !allLocationDtos || allLocationDtos.length === 0) return true;
    if (!studentsHomeSchooled) return true;
    return false;
};

const fetchAndBuildApp = async (
    location: LocationDto,
    schoolYearKey: string | number,
    studentNumber: IdField,
    firstName: string | undefined,
    studentsHomeSchooled: HomeSchooledEntry[] | undefined,
    signal: AbortSignal
): Promise<AppObject | null> => {
    if (getIsHomeSchooled(studentsHomeSchooled, studentNumber)) return null;

    const { transObj: app, window } = await fetchTransportationApp(location, schoolYearKey, studentNumber, signal);
    if (!app || !window) return null;

    // These two data-fetching operations are independent and can be run in parallel
    // for a minor performance improvement.
    const [csa, hasApplied] = await Promise.all([
        fetchCsa(studentNumber, signal),
        fetchHasApplied(studentNumber, schoolYearKey, signal)
    ]);

    return getBusPassStatus(app, csa, hasApplied, window, firstName);
};

const fetchTransportationApp = async (
    locationDto: LocationDto,
    schoolYearKey: string | number,
    studentNumber: IdField,
    signal: AbortSignal
): Promise<{ transObj: AppObject | null; window: TransportationWindowPayload | null }> => {
    try {
        const busRoute = await fetchBusRoute(studentNumber, signal);
        if (!busRoute) return { transObj: null, window: null };

        const response = await EcheckinDao<{ payload: TransportationWindowPayload }>({
            action: "checkinWindowTransportation",
            params: { locationId: locationDto.key },
            schoolYearKey,
            signal
        });

        if (!response?.data?.payload) return { transObj: null, window: null };

        const { payload } = response.data;
        const { checkinEndDate, checkinStartDate } = payload;
        const startEpoch = getEpoch(checkinStartDate);
        const todayEpoch = getEpoch(getTodayWithTime());

        const busNotice = [
            "Global_Notice_Bus",
            formatDate(checkinStartDate, false, true) ?? "",
            "Global_Notice_Check_Back"
        ];

        const transObj: AppObject = {
            app: "TRANSPORTATION",
            body: "BusApp_Body1",
            buttons: getButtons(checkinStartDate, checkinEndDate, studentNumber),
            endDate: formatDate(checkinEndDate, true, true),
            icon: "TRANSPORTATION_ICON",
            moreInfo: null,
            notice: startEpoch !== null && todayEpoch !== null && todayEpoch < startEpoch ? busNotice : null,
            startDate: formatDate(checkinStartDate, true, true),
            status: null,
            statusColor: "#0669B3",
            statusNumber: null,
            subBody: null,
            subStatus: null,
            subStatusRange: null,
            title: "BusApp_Header"
        };

        return { transObj, window: payload };
    } catch (error: unknown) {
        if (!axios.isCancel(error)) {
            console.error("Unable to retrieve Transportation Checkin Windows:\n", error);
        }
        return { transObj: null, window: null };
    }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Custom hook to determine if a student's guardian has access to the Transportation app tile
 * @name useBusApp
 * @param {boolean} isGuardian
 * @param {BusStudent} student
 * @param {boolean} enabled
 * @returns {{ appObj: AppObject | null, loading: boolean }}
 */
export const useBusApp = (
    isGuardian: boolean,
    student: BusStudent,
    enabled = true
): { appObj: AppObject | null; loading: boolean } => {
    const context = useContext(GlobalContext);
    const { dispatch, state } = context ?? {};
    const { allLocationDtos, schoolYearDto, studentAppObjects, studentsHomeSchooled } =
        (state as {
            allLocationDtos?: LocationDto[];
            schoolYearDto?: SchoolYearDto;
            studentAppObjects?: Record<string, Record<string, { appObj: AppObject | null }>>;
            studentsHomeSchooled?: HomeSchooledEntry[];
        }) || {};

    const { key: studentKey, studentNumber, firstName, locationKey, schoolId } = student || {};

    const [appObj, setAppObj] = useState<AppObject | null>(null);
    const [loading, setLoading] = useState(true);

    const studentAppObjectsRef = useRef(studentAppObjects);
    useEffect(() => {
        studentAppObjectsRef.current = studentAppObjects;
    }, [studentAppObjects]);

    const isFetching = useRef(false);

    const dispatchAppObject = useCallback(
        (obj: AppObject | null) => {
            const currentAppObjects = studentAppObjectsRef.current;
            const currentStored = currentAppObjects?.[studentKey!]?.TRANSPORTATION?.appObj;
            if (JSON.stringify(currentStored) === JSON.stringify(obj)) return;

            const payload = {
                ...currentAppObjects,
                [studentKey!]: {
                    ...currentAppObjects?.[studentKey!],
                    TRANSPORTATION: { appObj: obj }
                }
            };
            dispatch?.({ type: "StudentAppObjects", studentAppObjects: payload });
        },
        [dispatch, studentKey]
    );

    const existing = studentKey ? studentAppObjects?.[studentKey]?.TRANSPORTATION : null;

    const isExistingNull = existing === null;
    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        let isActive = true;

        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
            return;
        }

        if (shouldSkip(studentNumber, enabled, schoolYearDto, allLocationDtos, studentsHomeSchooled, isGuardian)) {
            setAppObj(null);
            setLoading(false);
            return;
        }

        if (isFetching.current) return;

        const run = async (): Promise<void> => {
            if (isActive) setLoading(true);
            try {
                isFetching.current = true;
                const location = getLocationDto(allLocationDtos!, schoolId, locationKey, studentNumber);
                const result = location
                    ? await fetchAndBuildApp(
                          location,
                          schoolYearDto!.key,
                          studentNumber,
                          firstName,
                          studentsHomeSchooled,
                          signal
                      )
                    : null;

                if (isActive && !signal.aborted) {
                    setAppObj(result);
                    dispatchAppObject(result);
                }
            } catch (error: unknown) {
                if (!axios.isCancel(error) && isActive) {
                    console.error("Unable to retrieve Transportation App:\n", error);
                }
            } finally {
                if (isActive) {
                    isFetching.current = false;
                    setLoading(false);
                }
            }
        };

        void run();

        return (): void => {
            isActive = false;
            isFetching.current = false;
        };
    }, [
        studentNumber,
        schoolYearDto,
        allLocationDtos,
        studentsHomeSchooled,
        isGuardian,
        enabled,
        firstName,
        locationKey,
        schoolId,
        existing,
        isExistingNull,
        dispatchAppObject
    ]);

    return { appObj, loading };
};
